import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  DescriptionList,
  LoadingState,
  Modal,
  Tabs,
  Text,
} from "@marekl/adminer/components";
import {
  usePreviewTemplateDraft,
  type PreviewResponse,
  type TemplateKind,
  type TemplateVersion,
} from "../../lib/queries/templates";
import { ApiError } from "../../lib/api";
import styles from "./TemplateDetailPage.module.css";

interface VersionPreviewModalProps {
  version: TemplateVersion;
  kind: TemplateKind;
  /** The template's channel; an SMS version has only a text body. */
  channel: "email" | "sms";
  /** Parsed sample data of the template (used for the render). */
  sampleData: unknown;
  onClose: () => void;
}

type BodyTab = "html" | "text";

/**
 * Read-only view of a historic version: subject/preheader plus a render via
 * POST /api/templates/preview with that version's stored content — works for
 * both kinds because preview accepts raw content.
 */
export function VersionPreviewModal({
  version,
  kind,
  channel,
  sampleData,
  onClose,
}: VersionPreviewModalProps) {
  const preview = usePreviewTemplateDraft();
  const [result, setResult] = useState<PreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<BodyTab>("html");
  const mutateAsync = preview.mutateAsync;

  useEffect(() => {
    let cancelled = false;
    mutateAsync({
      kind,
      channel,
      subject_tmpl: version.subject_tmpl,
      preheader_tmpl: version.preheader_tmpl,
      blocks_json: version.blocks_json,
      theme_json: version.theme_json,
      html_body: version.html_body,
      text_body: version.text_body,
      data: sampleData,
    })
      .then((res) => {
        if (!cancelled) setResult(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          const body = err.body as { message?: unknown } | null;
          if (body && typeof body.message === "string") {
            setError(body.message);
            return;
          }
        }
        setError("The version preview failed to render.");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- render once per version; the other inputs (kind, sampleData) are fixed for the life of the modal
  }, [version.id]);

  return (
    <Modal
      open
      title={`Version ${version.version}`}
      onClose={onClose}
      width={760}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className={styles.versionPreviewStack}>
        <DescriptionList labelWidth={120}>
          <DescriptionList.Item label="Status">
            {version.published ? "Published" : "Draft"}
          </DescriptionList.Item>
          <DescriptionList.Item label="Subject">
            {(result?.subject ?? version.subject_tmpl) || (
              <Text variant="muted">—</Text>
            )}
          </DescriptionList.Item>
          {(result?.preheader || version.preheader_tmpl) && (
            <DescriptionList.Item label="Preheader">
              {result?.preheader ?? version.preheader_tmpl}
            </DescriptionList.Item>
          )}
        </DescriptionList>

        {error && <Alert variant="err">{error}</Alert>}

        {!result && !error && <LoadingState label="Rendering preview…" />}

        {result && (
          <div>
            <Tabs
              aria-label="Version body"
              tabs={[
                { id: "html", label: "HTML" },
                { id: "text", label: "Text" },
              ]}
              value={tab}
              onChange={(v) => setTab(v as BodyTab)}
            />
            {tab === "html" ? (
              <iframe
                className={styles.versionFrame}
                sandbox=""
                srcDoc={result.html}
                title={`Preview of version ${version.version}`}
              />
            ) : (
              <pre className={styles.previewText}>
                {result.text || "(empty text part)"}
              </pre>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
